import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useEvenement } from '../data/queries';
import { trackEvent } from '../lib/track';
import { MTCLogo } from './Logo';

type Props = {
  theme?: 'light' | 'dark';
};

const NAV_ITEMS = [
  { to: '/formations', label: 'Formations' },
  { to: '/calendrier', label: 'Calendrier' },
  { to: '/formateurs', label: 'Formateurs' },
  { to: '/evenements', label: 'Événements' },
  { to: '/galerie', label: 'Galerie' },
  { to: '/publications', label: 'Publications' },
  { to: '/a-propos', label: 'À propos' },
] as const;

// `/membre` is intentionally omitted from nav until member auth is real
// (the route still resolves so any inscription redirect doesn't 404).

export function Header({ theme = 'light' }: Props) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const memberHref = user ? '/mon-espace' : '/connexion';
  const memberLabel = user ? 'Mon espace' : 'Connexion';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isDark = theme === 'dark';
  const isActive = (to: string) => pathname === to || pathname.startsWith(to + '/');

  // On an event detail / inscription page, organisers can replace the MTC logo
  // in the header with their own logo(s). Falls back to MTC when none set.
  const eventMatch = pathname.match(/^\/evenements\/([^/]+)(?:\/inscription)?\/?$/);
  const eventId = eventMatch?.[1];
  const headerEventQ = useEvenement(eventId);
  const orgLogos =
    eventId && headerEventQ.data?.organizerLogos?.length ? headerEventQ.data.organizerLogos : [];

  return (
    <header
      className={`mtc-header ${scrolled ? 'is-scrolled' : ''} ${isDark ? 'is-dark' : ''}`}
    >
      <div className="mtc-header-inner">
        {orgLogos.length > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(14px, 3.4vw, 32px)' }}>
            {orgLogos.map((url, i) => (
              <img
                key={`${url}-${i}`}
                src={url}
                alt=""
                style={{
                  height: 'clamp(65px, 8.75vw, 95px)',
                  width: 'auto',
                  maxWidth: 'clamp(138px, 32vw, 263px)',
                  objectFit: 'contain',
                }}
              />
            ))}
          </div>
        ) : (
          <Link to="/" className="mtc-link-reset" aria-label="Accueil">
            <MTCLogo mono={isDark} size={52} />
          </Link>
        )}
        <nav className="mtc-nav-desktop">
          {NAV_ITEMS.map((it) => (
            <button
              key={it.to}
              type="button"
              className={`mtc-nav-link ${isActive(it.to) ? 'is-active' : ''}`}
              onClick={() => {
                if (it.to === '/publications') trackEvent('publications_open');
                navigate(it.to);
              }}
            >
              {it.label}
            </button>
          ))}
        </nav>
        <div className="mtc-header-cta">
          <button
            type="button"
            className="mtc-nav-link"
            onClick={() => navigate(memberHref)}
            aria-label={memberLabel}
            style={{ marginRight: 4 }}
          >
            {memberLabel}
          </button>
          <button
            type="button"
            className="mtc-btn mtc-btn-primary"
            onClick={() => {
              trackEvent('sinscrire_click');
              navigate('/inscription');
            }}
          >
            S'inscrire
          </button>
        </div>
        <button
          type="button"
          className="mtc-burger"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Menu"
        >
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path
              d="M3 6h16M3 11h16M3 16h16"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      {mobileOpen && (
        <div className="mtc-mobile-menu">
          {NAV_ITEMS.map((it) => (
            <button
              key={it.to}
              type="button"
              className="mtc-mobile-link"
              onClick={() => {
                if (it.to === '/publications') trackEvent('publications_open');
                navigate(it.to);
                setMobileOpen(false);
              }}
            >
              {it.label}
            </button>
          ))}
          <button
            type="button"
            className="mtc-mobile-link"
            onClick={() => {
              navigate(memberHref);
              setMobileOpen(false);
            }}
          >
            {memberLabel}
          </button>
          <button
            type="button"
            className="mtc-btn mtc-btn-primary mtc-btn-block"
            onClick={() => {
              trackEvent('sinscrire_click');
              navigate('/inscription');
              setMobileOpen(false);
            }}
          >
            S'inscrire
          </button>
        </div>
      )}
    </header>
  );
}
